import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { useTheme } from 'styled-components';

import { PatchGeodataRequestBody } from '../../../../../../typings/api/skymap/rest/v0/geodata';
import { formattedDateTime } from '../../../../utils/dateUtils';
import { isValidNumber } from '../../../../utils/math/number';
import { gsdFromMeters, gsdToMeters } from '../../../../utils/proj4/units';
import { isDefined } from '../../../../utils/variables';
import { useDialog } from '../../../hooks/use-dialog';
import { useGsdUnit } from '../../../hooks/use-gsd-unit';
import { Button } from '../../button/button';
import { CheckBox } from '../../check-box/check-box';
import { Dialog } from '../../dialog/dialog';
import { Icon } from '../../icon/icon';
import { InfoBox } from '../../info-box/info-box';
import { LabelledContainer } from '../../labelled-container/labelled-container';
import { Stack } from '../../stack/stack';
import { TextBox } from '../../text-box/text-box';
import { AlignerContext } from '../gcp-page/aligner-state';
import { GeodataContext } from '../geodata-state';
import { AddOnData, AddOns } from '../new-cloud-process-dialog/add-ons';
import {
  GeodataConfiguration,
  GeodataConfigurationChangeData,
  GeodataConfigurationData,
} from './geodata-configuration';
import { useFetchGcpCorrectionJobQuery } from './use-fetch-gcp-correction-job-query';
import { usePushGcpCorrectionJobMutation } from './use-push-gcp-correction-job-mutation';
import { useSaveGeodataMutation } from './use-save-geodata-mutation';

export const GeodataSettingsDialog = (props: { dialog: ReturnType<typeof useDialog> }) => {
  const confirmButtonRef = useRef<HTMLButtonElement>(null);
  const { geodata } = useContext(GeodataContext);
  const { t } = useTranslation();
  const { showOrganizationAdminModules } = useContext(AlignerContext);
  const gsdUnit = useGsdUnit();

  const [configurationData, setConfigurationData] = useState<
    GeodataConfigurationChangeData | undefined
  >();
  const [addons, setAddons] = useState<AddOnData>(() => {
    const initialAddons: AddOnData = {
      terrainModel: {
        enabled: geodata.terrainModel,
      },
      filteredPointCloud: geodata.filteredPointCloud
        ? {
            enabled: true,
            classes: geodata.filteredPointCloud.class,
            format: geodata.filteredPointCloud.format,
            spacing: Number(geodata.filteredPointCloud.spacing),
          }
        : { enabled: false },
    };
    if (geodata.gcpAdjustmentRequest) {
      initialAddons.gcpAdjustment = {
        enabled: false,
      };
    }
    return initialAddons;
  });

  useEffect(() => {
    setConfigurationData({
      formData: {
        name: geodata.name,
        gsd: gsdFromMeters(geodata.gsd, gsdUnit),
        objMaxTriangles: geodata.objMaxTriangles?.toString(),
        rollingShutter: geodata.rollingShutter,
      },
      valid: true,
    });
    const updatedAddons: AddOnData = {
      terrainModel: {
        enabled: geodata.terrainModel,
      },
      filteredPointCloud: geodata.filteredPointCloud
        ? {
            enabled: true,
            classes: geodata.filteredPointCloud.class,
            format: geodata.filteredPointCloud.format,
            spacing: geodata.filteredPointCloud.spacing,
          }
        : { enabled: false },
    };
    if (geodata.gcpAdjustmentRequest) {
      updatedAddons.gcpAdjustment = {
        enabled: false,
      };
    }
    setAddons(updatedAddons);
  }, [geodata, gsdUnit]);

  const { saveQuery, errorList } = useSaveGeodataMutation({
    geodataId: geodata.id,
    onSuccess: () => props.dialog.hide(),
  });

  const handleAddOnsChange = (data: AddOnData) => {
    setAddons({
      ...addons,
      ...data,
      // Merge nested objects to preserve existing properties
      gcpAdjustment: data.gcpAdjustment
        ? {
            ...addons?.gcpAdjustment,
            ...data.gcpAdjustment,
          }
        : addons?.gcpAdjustment,
      filteredPointCloud: data.filteredPointCloud
        ? {
            ...addons?.filteredPointCloud,
            ...data.filteredPointCloud,
          }
        : addons?.filteredPointCloud,
    });
  };

  const handleConfirm = () => {
    if (!configurationData) {
      return;
    }

    const formData = configurationData.formData;
    // Keep the stored meters when the GSD is unchanged, rather than the rounded value the form shows.
    const gsdUnchanged = formData.gsd === gsdFromMeters(geodata.gsd, gsdUnit);
    const options: PatchGeodataRequestBody['options'] = {
      gsd: gsdUnchanged ? geodata.gsd : gsdToMeters(parseFloat(formData.gsd), gsdUnit),
      rollingShutter: formData.rollingShutter,
    };

    if (
      isDefined(formData.objMaxTriangles) &&
      formData.objMaxTriangles.length > 0 &&
      isValidNumber(formData.objMaxTriangles)
    ) {
      options.objMaxTriangles = parseInt(formData.objMaxTriangles);
    }

    // Add addon options
    if (addons.terrainModel?.enabled !== undefined) {
      options.createTerrainModel = addons.terrainModel.enabled;
    }

    if (addons.filteredPointCloud) {
      if (addons.filteredPointCloud.enabled) {
        options.filteredPointCloud = {
          classes: addons.filteredPointCloud.classes ?? 'ALL',
          spacing: addons.filteredPointCloud.spacing ?? 0.02,
          format: addons.filteredPointCloud.format ?? 'DXF',
        };
      } else {
        options.filteredPointCloud = null;
      }
    }

    const body: PatchGeodataRequestBody = {
      name: formData.name,
      options,
      // Keep existing gcpCollection unchanged
      gcpCollection: geodata.gcpCollection ?? undefined,
    };

    saveQuery.mutate(body);
  };

  const defaultValues = useMemo<GeodataConfigurationData>(() => {
    return {
      gcpFilename: geodata.gcpCollection?.name,
      name: geodata.name,
      objMaxTriangles: geodata.objMaxTriangles,
      gsd: gsdFromMeters(geodata.gsd, gsdUnit),
      rollingShutter: geodata.rollingShutter,
      terrainModel: geodata.terrainModel,
      gcpPoints: geodata.gcpCollection?.points.map((x) => {
        return {
          name: { value: x.name, status: 'valid' },
          e: { value: x.enu[0].toString(), status: 'valid_geoid' },
          n: { value: x.enu[1].toString(), status: 'valid_geoid' },
          h: { value: x.enu[2].toString(), status: 'valid_format' },
          included: true,
        };
      }),
    } as GeodataConfigurationData;
  }, [geodata, gsdUnit]);

  return (
    <Dialog closeIcon={true} height={'70vh'} maxWidth={'90%'} onClose={props.dialog.hide}>
      {{
        header: t('settings.title', { ns: 'cloudProcessing' }),
        content: (
          <Stack height="100%">
            <Content>
              <Stack direction="row" spacing={2}>
                <StyledLabelledContainer
                  fontWeight={500}
                  text={t('settings.title', { ns: 'cloudProcessing' })}
                >
                  <GeodataConfiguration
                    defaultValues={defaultValues}
                    processType={geodata.processType}
                    onChanged={setConfigurationData}
                  />
                </StyledLabelledContainer>
                <StyledLabelledContainer
                  fontWeight={500}
                  text={t('geodataSetup.addons', { ns: 'cloudProcessing' })}
                >
                  <AddOns
                    filteredPointCloud={addons?.filteredPointCloud?.enabled ?? false}
                    filteredPointCloudClasses={addons?.filteredPointCloud?.classes}
                    filteredPointCloudFormat={addons?.filteredPointCloud?.format}
                    filteredPointCloudSpacing={addons?.filteredPointCloud?.spacing}
                    terrainModel={addons?.terrainModel?.enabled ?? false}
                    onChange={handleAddOnsChange}
                  />
                </StyledLabelledContainer>
                {showOrganizationAdminModules && <SendGcpCorrectionJob geodataId={geodata.id} />}
              </Stack>
            </Content>
            {errorList}
          </Stack>
        ),
        footer: {
          right: (
            <>
              <Button
                color="primary"
                disabled={saveQuery.isPending}
                loading={saveQuery.isPending}
                ref={confirmButtonRef}
                variant="contained"
                onClick={handleConfirm}
              >
                {t('confirm', { ns: 'common' })}
              </Button>
              <Button variant="text" onClick={props.dialog.hide}>
                {t('close', { ns: 'common' })}
              </Button>
            </>
          ),
        },
      }}
    </Dialog>
  );
};

const Content = styled.div`
  flex: 1;
  overflow-y: auto;
`;

const StyledLabelledContainer = styled(LabelledContainer)`
  width: 500px;
`;

const SendGcpCorrectionJob = (props: { geodataId: string }) => {
  const [isUrgent, setIsUrgent] = useState(false);
  const [selfReview, setSelfReview] = useState(false);
  const [note, setNote] = useState('');
  const { t } = useTranslation();
  const theme = useTheme();
  const { mutate: sendGcpCorrectionJob, isPending, errorList } = usePushGcpCorrectionJobMutation();
  const { gcpCorrectionJobRequest } = useFetchGcpCorrectionJobQuery(props.geodataId);

  useEffect(() => {
    if (gcpCorrectionJobRequest !== undefined) {
      setSelfReview(gcpCorrectionJobRequest.selfReview);
    }
  }, [gcpCorrectionJobRequest]);

  const handleSend = () => {
    sendGcpCorrectionJob({
      path: { geodataId: props.geodataId },
      body: { isUrgent: isUrgent, note: note, selfReview: selfReview },
    });
  };

  return (
    <LabelledContainer
      fontWeight={500}
      text={t('settings.sendGcpCorrectionJob', { ns: 'cloudProcessing' })}
    >
      <Stack spacing={1}>
        <TextBox
          placeholder={t('settings.notePlaceholder', { ns: 'cloudProcessing' })}
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
        <CheckBox checked={selfReview} onChange={(e) => setSelfReview(e.target.checked)}>
          {t('settings.selfReview', { ns: 'cloudProcessing' })}
        </CheckBox>
        <CheckBox checked={isUrgent} onChange={(e) => setIsUrgent(e.target.checked)}>
          {t('settings.urgent', { ns: 'cloudProcessing' })}
        </CheckBox>
        {errorList}
        <InfoBox color="blue">{t('settings.selfReviewInfo', { ns: 'cloudProcessing' })}</InfoBox>
        <Button
          color="primary"
          disabled={isPending}
          leftIcon={{ icon: 'paper-plane' }}
          loading={isPending}
          variant="contained"
          onClick={handleSend}
        >
          {t('settings.sendGcpCorrectionJob', { ns: 'cloudProcessing' })}
        </Button>
        {gcpCorrectionJobRequest && gcpCorrectionJobRequest.updatedAt && (
          <Stack alignItems="center" direction="row" spacing={0.5}>
            <Icon color={theme.color.green} icon={['fad', 'check']} />
            <span>
              {t('settings.jobWasSent', { ns: 'cloudProcessing' })}{' '}
              {formattedDateTime(gcpCorrectionJobRequest.updatedAt)}
            </span>
          </Stack>
        )}
      </Stack>
    </LabelledContainer>
  );
};
